import { MapType } from 'lib/directions'

import { Position } from '../data/model'
import { serializeS } from '../serialize-s'
import { removeHereIdPrefix } from '../util'

export interface Place {
  id: string
  category: string
  title: string
  position: Position
  phone: string
  view?: View
}

export interface Location {
  position: Position
  customTitle?: string
}

export enum View {
  map = 'map',
  pdc = 'pdc',
}

export enum DeepLinkType {
  place = 'p',
  location = 'l',
}

/**
 * A function that gets the place, and other optional parameters, and returns a deep link either for places or locations.
 *
 * @param data - The place or the location object.
 * @param zoom - The zoom level of the map.
 * @param mapType - The map type of the map.
 *
 */
export function createDeepLink(data: Place | Location, zoom?: number, mapType?: MapType): string {
  const type = __isPlace(data) ? DeepLinkType.place : DeepLinkType.location
  const url = new URL(`https://share.here.com/${type}/`)
  const isZoomValid = zoom !== undefined && zoom >= 0 && zoom <= 20

  if (__isPlace(data)) {
    const placeS = serializeS({
      c: data.category.replace(/ /g, '+'),
      id: removeHereIdPrefix(data.id),
      lat: data.position.lat,
      lon: data.position.lng,
      n: data.title.replace(/ /g, '+'),
      ph: data.phone,
    })
    url.pathname += `${placeS}`

    data.view !== undefined && url.searchParams.set('v', data.view)
  } else {
    const title = data.customTitle !== undefined ? encodeURI(`,${data.customTitle}`) : ''
    url.pathname +=
      data.position !== undefined ? `${data.position.lat},${data.position.lng}${title}` : ''
  }

  isZoomValid && url.searchParams.set('z', String(zoom))
  mapType && url.searchParams.set('t', mapType)

  return url.toString()
}

function __isPlace(data: Place | Location): data is Place {
  return 'category' in data
}
